import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import './Architects.css';

const Architects = () => {
    const [isLoaded, setIsLoaded] = useState(false);

    useEffect(() => {
        setIsLoaded(true);
    }, []);

    return (
        <div className={`page architects-page ${isLoaded ? 'loaded' : ''}`}>
            {/* Hero */}
            <section className="page-hero">
                <div className="container">
                    <span className="label">For Architects & Designers</span>
                    <h1>Technology that respects your design intent.</h1>
                    <p className="page-hero-subtitle">
                        Professional documentation. Early coordination.
                        A partner who understands that the best technology disappears.
                    </p>
                    <div className="page-hero-cta">
                        <Link to="/contact" className="btn btn-primary">Discuss a project</Link>
                    </div>
                </div>
            </section>

            {/* Philosophy */}
            <section className="philosophy-section">
                <div className="container">
                    <div className="philosophy-content">
                        <h2>We believe automation should serve architecture, not compete with it.</h2>
                        <p>
                            You've spent months perfecting sight lines, material palettes, and the
                            way light moves through a space. The last thing you need is a technology
                            partner who wants to install visible equipment, clunky keypads, and
                            blinking lights that undermine everything you've designed.
                        </p>
                        <p>
                            We start from the opposite premise: if you notice the technology, we've
                            failed. Our job is to engineer systems that enhance the experience of
                            your architecture without ever announcing their presence.
                        </p>
                    </div>
                </div>
            </section>

            {/* Pain Points */}
            <section className="pain-section dark">
                <div className="container">
                    <div className="pain-header">
                        <span className="label">Common Frustrations</span>
                        <h2>What we hear from architects:</h2>
                    </div>

                    <div className="pain-quotes">
                        <blockquote>
                            "The integrator was brought in too late and wanted to change our ceiling design to accommodate speakers."
                        </blockquote>
                        <blockquote>
                            "I had to redo reflected ceiling plans three times because of coordination issues with low-voltage."
                        </blockquote>
                        <blockquote>
                            "My client called me two years after move-in because no one could figure out how to service the system."
                        </blockquote>
                        <blockquote>
                            "The documentation I received was a USB drive with 47 random PDFs and no organization."
                        </blockquote>
                    </div>
                </div>
            </section>

            {/* What We Provide */}
            <section className="provide-section">
                <div className="container">
                    <div className="provide-header">
                        <span className="label">What We Provide</span>
                        <h2>Documentation worthy of your drawing sets</h2>
                    </div>

                    <div className="provide-grid">
                        <div className="provide-card">
                            <h3>Technology Narrative</h3>
                            <p>
                                A plain-language document explaining the automation concept, system
                                capabilities, and user experience—suitable for client presentations
                                and project records.
                            </p>
                        </div>
                        <div className="provide-card">
                            <h3>Device Location Plans</h3>
                            <p>
                                Coordinated with your floor plans, showing exact placement of keypads,
                                sensors, speakers, and all visible devices. CAD files provided for
                                integration into your drawing sets.
                            </p>
                        </div>
                        <div className="provide-card">
                            <h3>Reflected Ceiling Coordination</h3>
                            <p>
                                Speaker locations, lighting control zones, and sensor placements
                                coordinated with your RCP before construction documents are finalized.
                            </p>
                        </div>
                        <div className="provide-card">
                            <h3>Elevation Details</h3>
                            <p>
                                Where automation devices interface with millwork or built-ins, we
                                provide elevation drawings showing exact dimensions and requirements.
                            </p>
                        </div>
                        <div className="provide-card">
                            <h3>Coordination Matrix</h3>
                            <p>
                                A clear chart showing every trade interface: what the electrician
                                provides, what we provide, what the millworker needs to know.
                            </p>
                        </div>
                        <div className="provide-card">
                            <h3>As-Built Records</h3>
                            <p>
                                Complete documentation package at project close: equipment inventory,
                                programming backups, network diagrams, and owner manuals.
                            </p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Collaboration Model */}
            <section className="collab-section">
                <div className="container">
                    <div className="collab-content">
                        <span className="label">Collaboration</span>
                        <h2>When to bring us in</h2>
                        <p>
                            The earlier, the better. We can add value as early as schematic design—helping
                            you understand what's possible and what infrastructure the design should
                            accommodate. By design development, we should be coordinating in earnest.
                        </p>
                        <p>
                            That said, we understand that's not always how projects unfold. If you're
                            already in CDs or even construction, we can still help—we'll just need to
                            work harder to avoid change orders.
                        </p>
                    </div>

                    <div className="collab-timeline">
                        <div className="collab-phase ideal">
                            <span className="phase-tag">Ideal</span>
                            <h4>Schematic Design</h4>
                            <p>Infrastructure planning, system concepts</p>
                        </div>
                        <div className="collab-phase ideal">
                            <span className="phase-tag">Ideal</span>
                            <h4>Design Development</h4>
                            <p>Full coordination, documentation begins</p>
                        </div>
                        <div className="collab-phase okay">
                            <span className="phase-tag">Workable</span>
                            <h4>Construction Docs</h4>
                            <p>Tight timeline but achievable</p>
                        </div>
                        <div className="collab-phase late">
                            <span className="phase-tag">Late</span>
                            <h4>Construction</h4>
                            <p>Change orders likely</p>
                        </div>
                    </div>
                </div>
            </section>

            {/* Trust */}
            <section className="trust-section">
                <div className="container">
                    <h2>Your client, your relationship.</h2>
                    <p>
                        We're here to make you look good, not to build our own client base through
                        yours. We work through you—presenting to your clients as an extension of
                        your team, not as a separate vendor pushing our own agenda.
                    </p>
                    <p>
                        When the project is complete, we'll support your clients directly for service
                        needs, but the relationship stays yours.
                    </p>
                </div>
            </section>

            {/* CTA */}
            <section className="page-cta">
                <div className="container">
                    <h2>Let's discuss how we can support your next project.</h2>
                    <p>
                        Whether you're designing a private residence or a boutique hospitality project,
                        we'd welcome the conversation.
                    </p>
                    <Link to="/contact" className="btn btn-primary">Get in touch</Link>
                </div>
            </section>
        </div>
    );
};

export default Architects;
